import React, { useCallback, useRef, useState } from 'react'
import { message } from 'antd'
import { Box, LinearProgress, Paper, Typography } from '@mui/material'

import { axiosInstance } from '../../../../hooks/useAxios'
import { fmtSize, IMAGE_MAX_SIZE, resizeImageFile } from './utils'

export interface UploadZoneProps {
  accept: string
  multiple?: boolean
  color: string
  icon: React.ReactNode
  title: string
  hint: string
  maxSize?: number
  onUpload?: (url: string, file?: File) => void
  onUploadMultiple?: (items: Array<{ url: string; alt?: string }>) => void
  disabled?: boolean
}

export const UploadZone: React.FC<UploadZoneProps> = ({
  accept,
  multiple,
  color,
  icon,
  title,
  hint,
  maxSize = IMAGE_MAX_SIZE,
  onUpload,
  onUploadMultiple,
  disabled,
}) => {
  const [drag, setDrag] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [progress, setProgress] = useState(0)
  const inputRef = useRef<HTMLInputElement>(null)

  const doUpload = useCallback(
    async (files: FileList | null) => {
      if (!files?.length) return
      const fileList = Array.from(files)
      const valid = fileList.filter((f) => f.size <= maxSize)
      if (valid.length !== fileList.length) {
        message.warning(`บางไฟล์เกินขนาด ${fmtSize(maxSize)}`)
      }
      if (valid.length === 0) return

      if (multiple && onUploadMultiple && valid.length > 0) {
        setUploading(true)
        try {
          const results: Array<{ url: string; alt?: string }> = []
          for (let i = 0; i < valid.length; i++) {
            setProgress(((i + 0.5) / valid.length) * 100)
            const fileToUpload = await resizeImageFile(valid[i])
            const formData = new FormData()
            formData.append('file', fileToUpload)
            const { data } = await axiosInstance.post<{ url: string }>(
              '/upload',
              formData,
              { headers: { 'Content-Type': 'multipart/form-data' } },
            )
            results.push({ url: data.url, alt: '' })
          }
          setProgress(100)
          onUploadMultiple(results)
          message.success(`อัพโหลด ${results.length} ไฟล์สำเร็จ`)
        } catch {
          message.error('อัพโหลดไม่สำเร็จ')
        } finally {
          setUploading(false)
          setProgress(0)
        }
        return
      }

      const file = valid[0]
      if (!onUpload) return
      setUploading(true)
      setProgress(0)
      try {
        const fileToUpload = await resizeImageFile(file)
        const formData = new FormData()
        formData.append('file', fileToUpload)
        const { data } = await axiosInstance.post<{ url: string }>(
          '/upload',
          formData,
          { headers: { 'Content-Type': 'multipart/form-data' } },
        )
        setProgress(100)
        onUpload(data.url, fileToUpload)
        message.success('อัพโหลดสำเร็จ')
      } catch {
        message.error('อัพโหลดไม่สำเร็จ')
      } finally {
        setUploading(false)
        setProgress(0)
      }
    },
    [maxSize, multiple, onUpload, onUploadMultiple],
  )

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    doUpload(e.target.files)
    e.target.value = ''
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setDrag(false)
    doUpload(e.dataTransfer.files)
  }

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    setDrag(true)
  }

  const handleDragLeave = () => setDrag(false)

  return (
    <Paper
      variant="outlined"
      onClick={() => !disabled && !uploading && inputRef.current?.click()}
      onDrop={handleDrop}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      sx={{
        border: '2px dashed',
        borderColor: drag ? color : 'divider',
        borderRadius: 2,
        p: 3,
        textAlign: 'center',
        cursor: disabled || uploading ? 'default' : 'pointer',
        pointerEvents: uploading ? 'none' : 'auto',
        bgcolor: drag ? `${color}08` : 'transparent',
        transition: 'all 0.2s',
        '&:hover': !disabled && !uploading ? { borderColor: color, bgcolor: `${color}08` } : {},
      }}
    >
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        multiple={multiple}
        onChange={handleChange}
        style={{ display: 'none' }}
      />
      <Box sx={{ color: drag ? color : 'text.secondary', mb: 1, '& .MuiSvgIcon-root': { fontSize: 40 } }}>
        {icon}
      </Box>
      <Typography variant="body2" fontWeight={600} color="text.primary">
        {title}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {hint}
      </Typography>
      {uploading && (
        <LinearProgress
          variant="determinate"
          value={progress}
          sx={{ mt: 1, borderRadius: 1 }}
        />
      )}
    </Paper>
  )
}
